/**
 * «ESTE WORD YA NO ES LA ÚLTIMA VERSIÓN», en todas las pestañas del panel (14-oct-2026).
 *
 * Cada fase, cada pasada del corrector y cada «Llevar a Word» dejan un Word nuevo, y el anterior sigue abierto. Rais:
 * «no sé si el usuario va a entender que tiene que abrir el documento y no volver atrás donde tenía el otro». Hasta hoy
 * solo lo decía la pestaña Reescritura; ahora, arriba, en las demás, con el botón para abrir la última.
 *
 * No habla con Word: pregunta al motor (\`GET /ultima-version\`) al abrir el panel, al cambiar de documento y al cambiar de
 * pestaña. Un motor anterior no tiene esa ruta: no hay aviso. En Reescritura y en Corrección no se enseña: allí ya lo dicen ellas.
 */
import { useCallback, useEffect, useState } from "react";
import { COMPANION } from "./companion";
import { hayQueAvisarDeVersion, fraseDeVersion, type UltimaVersion } from "./reescrituraLogica";

export type PuenteDeVersion = {
  mirar: (dir: string, doc: string) => Promise<UltimaVersion | null>;
  abrir: (dir: string, doc: string) => Promise<{ abierto: boolean; fichero?: string } | null>;
};
const DE_WORD = { "x-hi-cliente": "word" };
const PUENTE: PuenteDeVersion = {
  mirar: async (dir, doc) => {
    const r = await fetch(`${COMPANION}/ultima-version?${new URLSearchParams({ dir, doc })}`, { headers: DE_WORD });
    if (!r.ok) return null;
    const d = await r.json().catch(() => null);
    return d && typeof d === "object" && typeof d.hay === "boolean" ? (d as UltimaVersion) : null;
  },
  abrir: async (dir, doc) => {
    const r = await fetch(`${COMPANION}/reescribir/abrir`, { method: "POST", headers: { ...DE_WORD, "content-type": "application/json" }, body: JSON.stringify({ dir, doc, cual: "ultima" }) });
    const d = await r.json().catch(() => null);
    if (!r.ok) throw new Error(d?.error || "No he podido abrir la última versión.");
    return d;
  },
};

export const CSS_DEL_AVISO_DE_VERSION = `
.av-nota { font-size:13px; line-height:1.45; color:var(--hi-tinta); background:var(--hi-papel); border:1px solid var(--hi-aviso-raya); border-left-width:4px; border-radius:8px; padding:10px 12px; margin:4px 0 10px; overflow-wrap:anywhere; }
.av-nota p { margin:0 0 8px; }
.av-boton { font:inherit; font-size:13px; font-weight:800; padding:9px 12px; border-radius:8px; border:1px solid var(--hi-oficio); background:var(--hi-oficio); color:var(--hi-sobre-acento); cursor:pointer; }
.av-boton:focus-visible { outline:2px solid var(--hi-humano); outline-offset:2px; }
.av-boton:disabled { opacity:.5; cursor:not-allowed; }
.av-fallo { color:var(--hi-mal); font-size:12px; margin-top:6px; }
`;

export default function AvisoDeVersion({ dir, doc, tab, puente = PUENTE }: { dir: string; doc: string; tab: string; puente?: PuenteDeVersion }) {
  const [u, setU] = useState<UltimaVersion | null>(null);
  const [abriendo, setAbriendo] = useState(false);
  const [fallo, setFallo] = useState<string | null>(null);
  const mirar = useCallback(async () => {
    if (!dir || !doc) { setU(null); return; }
    try { setU(await puente.mirar(dir, doc)); } catch { setU(null); }
  }, [dir, doc, puente]);
  // Al abrir el panel, al cambiar de documento y al cambiar de pestaña: mientras tanto puede haber salido una versión nueva.
  useEffect(() => { setFallo(null); void mirar(); }, [mirar, tab]);
  if (!hayQueAvisarDeVersion(u)) return null;
  const abrir = async () => {
    setAbriendo(true); setFallo(null);
    try {
      const r = await puente.abrir(dir, doc);
      if (!r?.abierto) setFallo(`No he podido abrirla desde aquí. Está en la carpeta del libro, dentro de HumanInk: «${r?.fichero ?? u.ultima}».`);
    } catch (e) { setFallo(e instanceof Error ? e.message : String(e)); }
    finally { setAbriendo(false); }
  };
  return (
    <div className="av-nota" role="status">
      <style>{CSS_DEL_AVISO_DE_VERSION}</style>
      <p>{fraseDeVersion(u)}</p>
      <button className="av-boton" disabled={abriendo} onClick={() => void abrir()}>Abrir la última versión</button>
      {fallo && <div className="av-fallo" role="alert">{fallo}</div>}
    </div>
  );
}
